/**
 * The live call, as the desktop draws it (callbar.rs): one coloured strip —
 * green while the microphone is open, red when it is shut, grey when the
 * output is off, orange while connecting or when the link is bad — plus the
 * web's own additions under it: the R16 verification code with its "voice is
 * E2EE, chat is not" sentence, and the in-call roster with per-person
 * volume (D33's 0..2).
 */
import { For, type JSX, Show } from "solid-js";

import type { Dict } from "../lib/i18n";
import * as mut from "../store/mutators";
import type { Runtime } from "../store/runtime";
import type { VoiceCall } from "../store/types";
import { Icon } from "./ds";

/** callbar.rs's thresholds: where a blip becomes «a bad connection». */
const LOSS_BAD_PCT = 5.0;
const RTT_BAD_MS = 250;

type Tone = "live" | "muted" | "deafened" | "poor";

/** The desktop's `tone_of`, minus the native-only push-to-talk gate. */
function toneOf(voice: VoiceCall, t: Dict): { tone: Tone; state: string } {
  if (voice.phase !== "up") {
    return {
      tone: "poor",
      state: voice.code === null ? t.voiceConnecting() : t.voiceKeyExchange(),
    };
  }
  if (voice.deafened) return { tone: "deafened", state: t.voiceDeafenedState() };
  if (voice.lossPct >= LOSS_BAD_PCT) {
    return { tone: "poor", state: t.voicePoorLoss(voice.lossPct) };
  }
  if (voice.rttMs >= RTT_BAD_MS) return { tone: "poor", state: t.voicePoorRtt(voice.rttMs) };
  if (voice.muted) return { tone: "muted", state: t.voiceMutedState() };
  return { tone: "live", state: t.voiceLiveRtt(voice.rttMs) };
}

const TONE_CLASS: Record<Tone, string> = {
  live: "call-bar--live",
  muted: "call-bar--muted",
  deafened: "call-bar--deafened",
  poor: "call-bar--poor",
};

const TONE_ICON: Record<Tone, string> = {
  live: "graphic_eq",
  muted: "mic_off",
  deafened: "volume_off",
  poor: "sync_problem",
};

export function VoiceBar(props: { rt: Runtime; t: Dict; voice: VoiceCall }): JSX.Element {
  const rt = props.rt;
  const t = props.t;
  const s = rt.state;
  const tone = () => toneOf(props.voice, t);
  const channelName = () =>
    s.rooms.find((r) => r.channelId === props.voice.channelId)?.name ?? "";

  return (
    <div class="voice-panel">
      <div class={`call-bar ${TONE_CLASS[tone().tone]}`}>
        <Icon name={TONE_ICON[tone().tone]} size={17} />
        <div class="call-bar__text">
          <span class="call-bar__name">{channelName()}</span>
          <span class="call-bar__state">{tone().state}</span>
        </div>
        <button
          type="button"
          class="call-bar__button"
          title={props.voice.muted ? t.voiceUnmute() : t.voiceMute()}
          onClick={() => rt.toggleMute()}
        >
          <Icon name={props.voice.muted ? "mic_off" : "mic"} size={19} />
        </button>
        <button
          type="button"
          class="call-bar__button"
          title={props.voice.deafened ? t.voiceUndeafen() : t.voiceDeafen()}
          onClick={() => rt.toggleDeafen()}
        >
          <Icon name={props.voice.deafened ? "volume_off" : "volume_up"} size={19} />
        </button>
        <button
          type="button"
          class="call-bar__button call-bar__button--end"
          title={t.voiceDisconnect()}
          onClick={() => rt.leaveVoice()}
        >
          <Icon name="call_end" size={16} />
        </button>
      </div>

      <Show when={props.voice.code}>
        {(code) => (
          <div class="voice-code">
            <span class="voice-code__row">
              <Icon name="lock" size={13} />
              <span>
                {t.voiceCode()}: <b>{code()}</b>
              </span>
            </span>
            <span class="voice-code__note">{t.voiceE2eeNote()}</span>
          </div>
        )}
      </Show>

      <Show when={props.voice.peers.length > 0}>
        <div class="voice-peers">
          <For each={props.voice.peers}>
            {(peer) => (
              <div
                class={`voice-peer ${props.voice.speaking.includes(peer.userId) ? "voice-peer--speaking" : ""}`}
              >
                <span class="voice-peer__name">
                  {mut.nameOf(s.directory, peer.userId) !== `#${peer.userId}`
                    ? mut.nameOf(s.directory, peer.userId)
                    : peer.username}
                </span>
                <Show when={peer.muted || peer.serverMuted}>
                  <Icon name="mic_off" size={13} />
                </Show>
                <Show when={peer.deafened}>
                  <Icon name="volume_off" size={13} />
                </Show>
                <Show when={peer.screenSharing}>
                  <button
                    type="button"
                    class={`voice-peer__watch ${props.voice.share?.userId === peer.userId ? "voice-peer__watch--on" : ""}`}
                    title={
                      props.voice.share?.userId === peer.userId
                        ? t.stopWatching()
                        : t.watchStream()
                    }
                    onClick={() =>
                      props.voice.share?.userId === peer.userId
                        ? rt.stopWatching()
                        : rt.watchStream(peer.userId)
                    }
                  >
                    <Icon name="cast" size={13} />
                  </button>
                </Show>
                <input
                  class="voice-peer__volume"
                  type="range"
                  min="0"
                  max="200"
                  value={(props.voice.volumes[peer.userId] ?? 1) * 100}
                  title={`${Math.round((props.voice.volumes[peer.userId] ?? 1) * 100)}%`}
                  onInput={(e) =>
                    rt.setVoiceVolume(peer.userId, Number(e.currentTarget.value) / 100)
                  }
                />
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
